import { Button, Menu, MenuItem, Table, Typography } from "flux";
import { useParams } from "react-router-dom";
import { KeyedMutator } from "swr";

import { ProcessedAdAsset } from "./types";
import { AdAssetImage } from "../../types/ad-asset-image";
import { useDialog } from "@adora/shared/hooks/use-dialog";
import ImageUploadDialog from "./image-upload-dialog";
import { useState } from "react";
import useMutateAdAssets from "../../hooks/use-mutate-ad-asset";
import { AdAsset } from "../../types/ad-asset";

export interface AdAssetImagesTableProps {
  processedAdAssets: ProcessedAdAsset[];
  invalidateAdAssetsCache: KeyedMutator<AdAsset[]>;
  loading: boolean;
  deepLink: string | null;
}

const AdAssetImagesTable: React.FC<AdAssetImagesTableProps> = ({
  processedAdAssets,
  invalidateAdAssetsCache,
  loading,
  deepLink
}) => {
  const { brandId } = useParams<{ brandId: string }>();
  const { openDialog } = useDialog();

  const MediaPreviewCell: React.FC<{
    displayDeepLink?: boolean;
    image?: AdAssetImage;
  }> = ({ image, displayDeepLink }) => {
    if (displayDeepLink && deepLink) {
      return (
        <div style={{ width: 150, height: 150, display: "flex", alignItems: "center", justifyContent: "center" }}>
          <a href={`${deepLink}&assetId=${image?.id}`} target="_blank" rel="noopener noreferrer">
            {image?.id}
          </a>
        </div>
      );
    }
    if (!image) return <Typography color="textSecondary">No media</Typography>;

    const handleImageClick = async (e: React.MouseEvent<HTMLButtonElement>) => {
      e.preventDefault();
      await openDialog(ImageUploadDialog, { brandId, imageId: image.id, refetchAdAssets: invalidateAdAssetsCache });
    };

    return (
      <button onClick={handleImageClick} style={{ border: "none", background: "none", padding: 0, cursor: "pointer" }}>
        <img
          src={image.url}
          alt="Preview"
          width={150}
          height={150}
          style={{ objectFit: "contain", cursor: "pointer" }}
        />
      </button>
    );
  };

  const StatusCell: React.FC<{ status: string }> = ({ status }) => (
    <Typography
      color={status == "rejected" ? "error" : status == "approved" ? "success" : "textSecondary"}
      variant="body2"
    >
      {status}
    </Typography>
  );

  const ActionMenu: React.FC<{
    adAsset: ProcessedAdAsset;
    onRefetch: () => void;
  }> = ({ adAsset, onRefetch }) => {
    const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
    const { deleteAdAsset, isLoading: isDeleting } = useMutateAdAssets();
    const { brandId } = useParams<{ brandId: string }>();

    const handleMenuOpen = (event: React.MouseEvent<HTMLButtonElement>) => {
      setAnchorEl(event.currentTarget);
    };

    const handleMenuClose = () => {
      setAnchorEl(null);
    };

    const handleDelete = async () => {
      if (!brandId) return;

      const confirmed = window.confirm("Are you sure you want to delete this Ad Asset? This action cannot be undone.");
      if (confirmed) {
        const success = await deleteAdAsset(adAsset.id, Number(brandId));
        if (success) {
          onRefetch();
        }
        handleMenuClose();
      }
    };

    return (
      <>
        <Button
          onClick={handleMenuOpen}
          variant="text"
          color="inherit"
          size="small"
          dataDogIdentifier="ad asset images table ellipsis action"
        >
          ...
        </Button>
        <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={handleMenuClose}>
          <MenuItem onClick={handleDelete} style={{ color: "red" }} disabled={isDeleting}>
            {isDeleting ? "Deleting..." : "Delete"}
          </MenuItem>
        </Menu>
      </>
    );
  };

  const assetsColumns = [
    {
      field: "id",
      value: "ID",
      width: 80,
      renderCell: (row: ProcessedAdAsset) => (
        <div style={{ width: 150, height: 150, display: "flex", alignItems: "center", justifyContent: "center" }}>
          <a href={`${deepLink}&assetId=${row.id}`} target="_blank" rel="noopener noreferrer">
            {row.id}
          </a>
        </div>
      )
    },
    {
      field: "square_image_url",
      value: "Square Media",
      width: 200,
      renderCell: (row: ProcessedAdAsset) => <MediaPreviewCell image={row.square_image} />
    },
    {
      field: "landscape_image_url",
      value: "Landscape Media",
      width: 200,
      renderCell: (row: ProcessedAdAsset) => <MediaPreviewCell image={row.landscape_image} />
    },
    {
      field: "portrait_image_url",
      value: "Portrait Media",
      width: 200,
      renderCell: (row: ProcessedAdAsset) => <MediaPreviewCell image={row.portrait_image} />
    },
    {
      field: "feed_image_url",
      value: "Feed Media",
      width: 200,
      renderCell: (row: ProcessedAdAsset) => <MediaPreviewCell image={row.feed_image} />
    },
    {
      field: "comments",
      value: "Comments",
      width: 300,
      renderCell: (row: ProcessedAdAsset) => (row.comments.length > 0 || row.threads.length > 0 ? "true" : "false")
    },
    { field: "created_at", value: "Created At", width: 180 },
    { field: "updated_at", value: "Updated At", width: 180 },
    {
      field: "status",
      value: "Approval Status",
      width: 140,
      renderCell: (row: ProcessedAdAsset) => <StatusCell status={row.status} />
    },
    {
      field: "is_deleted",
      value: "Deleted?",
      width: 140,
      renderCell: (row: ProcessedAdAsset) => (row.is_deleted ? "Deleted" : "Not Deleted")
    },
    {
      field: "actions",
      value: "Actions",
      width: 100,
      renderCell: (row: ProcessedAdAsset) => <ActionMenu adAsset={row} onRefetch={invalidateAdAssetsCache} />
    }
  ];

  return (
    <Table columns={assetsColumns} rows={processedAdAssets} isLoading={loading} dataDogIdentifier="ad asset images" />
  );
};

export default AdAssetImagesTable;
